import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success('Welcome back!');
      // Redirect admins to admin dashboard
      if (user.is_admin) {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const user = await googleLogin(credentialResponse.credential);
      toast.success(user.needs_role ? 'Account created successfully!' : 'Welcome back!');
      if (user.needs_role) {
        navigate('/select-role');
      } else if (user.is_admin) {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Google login failed');
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: '#f7f5f2' }}>
      {/* Left side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative bg-black">
        <img
          src="https://images.pexels.com/photos/10252844/pexels-photo-10252844.jpeg?auto=compress&cs=tinysrgb&w=1280&q=80"
          alt="Creative workspace"
          className="absolute inset-0 w-full h-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <div className="absolute bottom-16 left-16 right-16 text-white">
          <p className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: '#d4a843' }}>Welcome back</p>
          <h2 className="mb-4" style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 'normal', fontSize: '3.5rem', lineHeight: 1.1, color: '#ffffff' }}>
            FrameFinder
          </h2>
          <p className="text-gray-300 text-lg font-light">Connect with amazing photographers and videographers</p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-fadeInUp">
          <Link to="/" className="inline-flex items-center gap-2 mb-12 transition-colors group" style={{ color: '#6b6b6b' }}>
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm">Back to home</span>
          </Link>

          <h1 className="mb-2" style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 'normal', fontSize: '2.5rem' }}>Sign in</h1>
          <p className="mb-10" style={{ color: '#6b6b6b' }}>Enter your credentials to access your account</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium" style={{ color: '#0e0e0e' }}>Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="h-12 rounded-lg"
                style={{ borderColor: '#e4e1db' }}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium" style={{ color: '#0e0e0e' }}>Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-sm transition-colors hover:underline"
                  style={{ color: '#d4a843' }}
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="h-12 rounded-lg"
                style={{ borderColor: '#e4e1db' }}
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 rounded-full font-medium text-white"
              style={{ background: '#0e0e0e' }}
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t" style={{ borderColor: '#e4e1db' }} />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4" style={{ background: '#f7f5f2', color: '#9a9a9a' }}>or</span>
            </div>
          </div>

          <div className="flex justify-center w-full overflow-hidden">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => toast.error('Google login failed')}
              useOneTap={false}
              theme="outline"
              size="large"
              width="100%"
            />
          </div>

          <p className="text-center mt-10" style={{ color: '#6b6b6b' }}>
            Don't have an account?{' '}
            <Link to="/signup" className="font-medium hover:underline" style={{ color: '#0e0e0e' }} data-testid="signup-link">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
